package com.dotdrives.web.pages.settings

import com.dotdrives.controller.*
import com.dotdrives.model.*
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.web.ui.*
import react.*
import kotlinx.browser.document
import web.dom.ElementId
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.form
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.h3
import web.cssom.ClassName
import web.html.InputType
import web.html.checkbox
import web.html.submit
import web.html.button

external interface MemberAccessPanelProps : Props {
    var member: OrgMember
    var members: List<OrgMember>
    var role: String
    var workflow: WorkflowController
    var workflowState: WorkflowState
}

val MemberAccessPanel = FC<MemberAccessPanelProps> { props ->
    val member = props.member
    val (newRole, setNewRole) = useState(member.role)
    val (removeOpen, setRemoveOpen) = useState(false)
    val (reassign, setReassign) = useState("")
    val (confirmed, setConfirmed) = useState(false)
    val wasRemoveOpen = useRef(false)
    useEffect(removeOpen) {
        if (removeOpen) document.getElementById("settings-member-reassign")?.asDynamic()?.focus()
        else if (wasRemoveOpen.current == true) document.getElementById("settings-member-remove-open")?.asDynamic()?.focus()
        wasRemoveOpen.current = removeOpen
    }
    useEffect(member.userId, member.role) {
        setNewRole(member.role); setReassign(""); setConfirmed(false); setRemoveOpen(false)
    }
    val locked = props.workflowState.busy || props.workflowState.pending != null
    val lastOwner = member.role == "owner" && props.members.count { it.role == "owner" && it.deactivatedAt == null } <= 1
    div {
        className = ClassName("settings-section-card settings-member-editor")
        div { className = ClassName("settings-section-title"); +member.displayName }
        if (member.deactivatedAt != null) div { +"This teammate's access is deactivated." }
        else if (canAdminister(props.role) && (member.role != "owner" || props.role == "owner")) {
            form {
                className = ClassName("settings-focused-form")
                onSubmit = {
                    it.preventDefault()
                    if (!locked && !lastOwner && newRole != member.role) props.workflow.perform("workflows", "member.role", mapOf("user_id" to member.userId, "role" to newRole))
                }
                label {
                    +"Role"
                    select {
                        value = newRole; disabled = locked || lastOwner
                        onChange = { setNewRole(it.currentTarget.value) }
                        (INVITABLE_ROLES + if (props.role == "owner") listOf("owner") else emptyList()).forEach {
                            option { value = it; +roleLabel(it) }
                        }
                    }
                }
                div {
                    className = ClassName("settings-editor-actions")
                    DotButton { text = "Cancel"; variant = DotButtonVariant.Secondary; enabled = !locked && newRole != member.role; onClick = { setNewRole(member.role) } }
                    button { type = web.html.ButtonType.submit; className = ClassName("dot-button dot-button-primary"); disabled = locked || lastOwner || newRole == member.role; +"Save role" }
                }
            }
            if (lastOwner) p { className = ClassName("settings-section-subtitle"); +"Add another active owner before changing this owner's role or removing access." }
            div {
                className = ClassName("settings-access-removal")
                h3 { +"Remove access" }
                p { +"Deactivate this teammate and transfer their active work. Historical authorship is preserved." }
                if (!removeOpen) button {
                    id = "settings-member-remove-open".unsafeCast<ElementId>()
                    type = web.html.ButtonType.button
                    className = ClassName("dot-button dot-button-secondary")
                    disabled = locked || lastOwner
                    onClick = { setRemoveOpen(true) }
                    +"Review access removal"
                } else {
                    label {
                        +"Reassign active work to"
                        select {
                            id = "settings-member-reassign".unsafeCast<ElementId>()
                            value = reassign; disabled = locked
                            onChange = { setReassign(it.currentTarget.value); setConfirmed(false) }
                            option { value = ""; +"Choose an active teammate" }
                            props.members.filter { it.userId != member.userId && it.deactivatedAt == null }.forEach { option { value = it.userId; +it.displayName } }
                        }
                    }
                    label {
                        input { type = InputType.checkbox; checked = confirmed; disabled = locked || reassign.isBlank(); onChange = { setConfirmed(it.currentTarget.checked) } }
                        +"Remove ${member.displayName}'s access and transfer their active work."
                    }
                    div {
                        className = ClassName("settings-editor-actions")
                        DotButton { text = "Cancel removal"; variant = DotButtonVariant.Secondary; enabled = !locked; onClick = { setRemoveOpen(false); setConfirmed(false); setReassign("") } }
                        DotButton {
                            text = "Deactivate and reassign"; variant = DotButtonVariant.Secondary; enabled = !locked && !lastOwner && confirmed && reassign.isNotBlank()
                            onClick = { props.workflow.perform("workflows", "member.deactivate", mapOf("user_id" to member.userId, "reassign_to" to reassign)) }
                        }
                    }
                }
            }
        }
    }
}
